/* HR — Expense (R4 fully wired)
 *
 *  Endpoints:
 *    GET    /api/expense/categories?active_only=
 *    POST   /api/expense/categories                    (finance/hr/admin)
 *    PATCH  /api/expense/categories/:id
 *    GET    /api/expense/claims?status=&page=
 *    GET    /api/expense/claims/me
 *    GET    /api/expense/claims/:id                    (含 items, approvals)
 *    POST   /api/expense/claims                        { employee_id, title?, claim_date?, currency?, payment_method?, remark? }
 *    PATCH  /api/expense/claims/:id                    (draft only)
 *    DELETE /api/expense/claims/:id                    (draft only)
 *    POST   /api/expense/claims/:id/items              { expense_date?, category_id?, amount, tax_amount, description?, invoice_no? }
 *    PATCH  /api/expense/items/:id
 *    DELETE /api/expense/items/:id
 *    POST   /api/expense/claims/:id/submit             draft → submitted
 *    POST   /api/expense/claims/:id/approve            { comment? }
 *    POST   /api/expense/claims/:id/reject             { comment }   (required)
 *    POST   /api/expense/claims/:id/pay                { payment_method?, paid_at?, remark? }  (finance/admin)
 */

const CLAIM_STATUS = {
  draft:     { label: '草稿',   tone: 'neutral' },
  submitted: { label: '審核中', tone: 'warn'    },
  approved:  { label: '已核准', tone: 'accent'  },
  rejected:  { label: '已退回', tone: 'down'    },
  paid:      { label: '已付款', tone: 'accent'  },
};

/* ── Expressive (GX) skin helpers — plan-expressive-inner-pages.md §2(d).
 * Render-time only; under Bento they return the ORIGINAL object (uiStyle), so Bento output is unchanged. */
// Uppercase / letter-spaced micro label → GX sentence-case 700 label (onV: AA on every tonal surface).
function _exGxLabel(bento) {
  return uiStyle(bento, { letterSpacing: 0, textTransform: 'none', fontWeight: 700, color: GxT.color.onV });
}
// Small info / summary box inside a modal or card → GX tonal s2 box, radius 12, no border.
function _exGxBox(bento) {
  return uiStyle(bento, { background: GxT.color.s2, borderRadius: GxT.radius.inner, border: 'none' });
}
// Italic serif text → GX upright (Figtree has no true italic).
function _exGxUpright(bento) {
  return uiStyle(bento, { fontStyle: 'normal' });
}
// Employee initial avatar → GX tertiary container (mockup .av).
function _exGxAvatar(bento) {
  return uiStyle(bento, { background: GxT.color.tc, color: GxT.color.onTc, fontWeight: 800 });
}

function ReceiptLink({ path }) {
  if (!path) return <span style={{ color: HR.inkFaint }}>—</span>;
  const url = `/api/expense/claims/receipt?path=${encodeURIComponent(path)}&token=${encodeURIComponent(getToken() || '')}`;
  const raw = path.split('/').pop() || '';
  const name = raw.replace(/^\d{8}T\d{6}Z_/, '');
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
       style={{ color: HR.accent, fontSize: FontSizes.xxs, fontFamily: HR.mono, textDecoration: 'underline' }}
       title={raw}>
      📎 {name.length > 20 ? name.slice(0, 18) + '…' : name}
    </a>
  );
}

/* 此頁 capability 約定（後端 routes_expense.py 對應）:
 *   expense.read.all          → 看別人單；缺則只看 /claims/me
 *   expense.approve           → 看「審核佇列」分頁 + 核准/退回按鈕
 *   expense.pay               → 看「待付款」區塊 + 標記付款按鈕
 *   expense.category.manage   → 看「類別管理」分頁 + CRUD 按鈕
 * 後端 require_role 依然強制；前端只做 UI 渲染決策。 */

/* ─────────────────────────  Claim composer (create new)  ──────── */

function makeTmpItem(categories) {
  return {
    tmpId: Math.random().toString(36).slice(2),
    category_id: categories[0]?.id || null,
    expense_date: todayStr(),
    amount: 0,
    description: '',
  };
}

/* 圖檔 → resize 至最長邊 maxDim → base64（不含 data: 前綴） */
async function fileToResizedBase64(file, maxDim = 1920, quality = 0.85) {
  const dataUrl = await new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result);
    reader.onerror = () => rej(new Error('讀取檔案失敗'));
    reader.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = () => rej(new Error('圖片格式無法解析'));
    i.src = dataUrl;
  });
  let { width, height } = img;
  const longest = Math.max(width, height);
  if (longest > maxDim) {
    const ratio = maxDim / longest;
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(img, 0, 0, width, height);
  const out = canvas.toDataURL('image/jpeg', quality);
  return out.split(',', 2)[1]; // 去掉 'data:image/jpeg;base64,' 前綴
}

/* ── ExpenseOcrModal ── 收據/發票 OCR 辨識導入 */
function ExpenseOcrModal({ categories, onCancel, onConfirm }) {
  const toast = useToast();
  const [ocrLoading, setOcrLoading] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [dragOver, setDragOver] = React.useState(false);
  const fileRef = React.useRef(null);

  const processFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast('只接受圖片檔', 'error'); return;
    }
    setOcrLoading(true);
    try {
      // 1. 先上傳收據圖檔
      const fd = new FormData();
      fd.append('file', file);
      const token = getToken();
      const uploadRes = await fetch('/api/expense/claims/receipt-upload', {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: fd,
      });
      const uploadData = await uploadRes.json().catch(() => ({}));
      if (!uploadRes.ok) {
        throw new Error(uploadData.detail || '收據憑證上傳失敗');
      }
      const uploadedPath = uploadData.path;

      // 2. 進行 OCR 辨識
      // fileToResizedBase64 一律輸出 JPEG，故 mime 固定 image/jpeg，
      // 不能用 file.type（上游以 mime 解碼，標錯會 decode 失敗）。
      const b64 = await fileToResizedBase64(file);
      const resp = await api('POST', '/expense/claims/ocr', { image: b64, mime: 'image/jpeg' });
      
      const parsedItems = (resp.items || []).map(item => {
        const matchedCat = categories.find(c => c.code === item.category_code) || categories[0];
        return {
          tmpId: Math.random().toString(36).slice(2),
          category_id: matchedCat ? matchedCat.id : null,
          expense_date: item.expense_date || todayStr(),
          amount: parseFloat(item.amount) || 0,
          description: item.description || '',
          invoice_no: item.invoice_no || '',
          attachment_path: uploadedPath, // 自動關聯已上傳的收據路徑
        };
      });

      setItems(parsedItems);
      toast(`已辨識出 ${parsedItems.length} 筆明細，收據已自動上傳並關聯`, 'ok');
    } catch (err) {
      toast(err.detail || err.message || 'OCR 辨識或上傳失敗', 'error');
    } finally {
      setOcrLoading(false);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    processFile(file);
  };

  const previewTotal = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);

  return (
    <Modal
      open onClose={onCancel}
      title="收據 OCR 導入"
      subtitle="支援發票、車票或收據圖片自動辨識"
      size="md"
      footer={
        items.length > 0 ? (
          <>
            <Button variant="ghost" onClick={onCancel} disabled={ocrLoading}>取消</Button>
            <Button variant="secondary" onClick={() => onConfirm(items, 'append')} disabled={ocrLoading}>累加導入</Button>
            <Button variant="primary" onClick={() => onConfirm(items, 'overwrite')} disabled={ocrLoading}>覆蓋導入</Button>
          </>
        ) : (
          <Button variant="ghost" onClick={onCancel}>取消</Button>
        )
      }
    >
      <input
        ref={fileRef} type="file" accept="image/*"
        style={{ display: 'none' }} onChange={handleFile}
      />

      {/* 步驟指示 */}
      <div style={{
        display: 'flex', gap: 6, alignItems: 'center',
        fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginBottom: 18,
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: items.length === 0 && !ocrLoading ? HR.ink : HR.accent, fontWeight: items.length === 0 && !ocrLoading ? 600 : 400 }}>
          ① 選擇照片
        </span>
        <span style={{ flex: 1, height: 1, background: HR.ruleSoft }} />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: ocrLoading ? HR.ink : (items.length > 0 ? HR.accent : HR.inkFaint), fontWeight: ocrLoading ? 600 : 400 }}>
          ② OCR 辨識
        </span>
        <span style={{ flex: 1, height: 1, background: HR.ruleSoft }} />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: items.length > 0 && !ocrLoading ? HR.ink : HR.inkFaint, fontWeight: items.length > 0 && !ocrLoading ? 600 : 400 }}>
          ③ 確認導入
        </span>
      </div>

      {/* 1. 上傳區 */}
      {items.length === 0 && !ocrLoading && (
        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault(); setDragOver(false);
            processFile(e.dataTransfer.files?.[0]);
          }}
          style={uiStyle({
            cursor: 'pointer',
            padding: '48px 20px', textAlign: 'center',
            border: `2px dashed ${dragOver ? HR.accent : HR.rule}`,
            borderRadius: 6,
            background: dragOver ? HR.accentSoft : HR.surfaceAlt,
            transition: 'border-color 120ms, background 120ms, transform 120ms',
            transform: dragOver ? 'scale(1.01)' : 'scale(1)',
          }, { borderRadius: GxT.radius.card })}
          onMouseEnter={(e) => {
            if (dragOver) return;
            e.currentTarget.style.borderColor = HR.accent;
            e.currentTarget.style.background = HR.accentSoft;
          }}
          onMouseLeave={(e) => {
            if (dragOver) return;
            e.currentTarget.style.borderColor = HR.rule;
            e.currentTarget.style.background = HR.surfaceAlt;
          }}
        >
          <div style={{ color: HR.accent, display: 'flex', justifyContent: 'center' }}>
            <Icon name="upload" size={36} stroke={1.5} />
          </div>
          <div style={{ marginTop: 12, fontSize: FontSizes.sm, fontWeight: 500, color: HR.ink }}>
            {dragOver ? '放開以上傳' : '點此選擇 或 拖拉收據圖片進來'}
          </div>
          <div style={{ marginTop: 6, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
            支援 JPG / PNG / WebP — 自動偵測消費日期、類別、金額與統編
          </div>
        </div>
      )}

      {/* 2. OCR loading */}
      {ocrLoading && (
        <div style={uiStyle({
          padding: '60px 20px', textAlign: 'center', color: HR.inkSoft,
          border: `2px dashed ${HR.rule}`, borderRadius: 6, background: HR.surfaceAlt,
        }, { borderRadius: GxT.radius.card })}>
          <Spinner size={22} />
          <div style={{ marginTop: 14, fontSize: FontSizes.xs, color: HR.ink, fontWeight: 500 }}>
            辨識中… 請稍候
          </div>
          <div style={{ marginTop: 4, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
            正在分析收據資訊
          </div>
        </div>
      )}

      {/* 3. 預覽 */}
      {items.length > 0 && !ocrLoading && (
        <div>
          <div style={uiStyle({
            marginBottom: 12, padding: '10px 14px',
            background: HR.accentSoft, color: HR.accent,
            borderRadius: 4, fontSize: FontSizes.xxs, lineHeight: 1.5,
            display: 'flex', alignItems: 'flex-start', gap: 8,
          }, { borderRadius: GxT.radius.inner, color: GxT.color.onPc, fontWeight: 600 })}>
            <span style={{ fontSize: FontSizes.sm, lineHeight: 1 }}>✓</span>
            <div>
              辨識完成！共讀取到 <strong style={{ fontFamily: HR.mono }}>{items.length}</strong> 筆明細，請確認：
            </div>
          </div>

          <div style={uiStyle({
            border: `1px solid ${HR.rule}`, borderRadius: 4,
            maxHeight: 280, overflowY: 'auto',
          }, { borderRadius: GxT.radius.inner })}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
              <thead>
                <tr style={{ background: HR.surfaceAlt, color: HR.inkFaint, fontSize: FontSizes.xxs, borderBottom: `1px solid ${HR.rule}` }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 500 }}>日期</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 500 }}>預測類別</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 500 }}>說明</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 500 }}>金額</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const cat = categories.find(c => c.id === it.category_id);
                  return (
                    <tr key={it.tmpId} style={{ borderBottom: i < items.length - 1 ? `1px solid ${HR.ruleSoft}` : 'none' }}>
                      <td style={{ padding: '8px 12px', fontFamily: HR.mono, color: HR.inkSoft }}>{it.expense_date}</td>
                      <td style={{ padding: '8px 12px' }}>{cat ? cat.name : '—'}</td>
                      <td style={{ padding: '8px 12px', color: HR.inkSoft }}>
                        {it.description}
                        {it.invoice_no && <div style={{ fontSize: uiPick('10px', 11), color: HR.inkFaint }}>發票:{it.invoice_no}</div>}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 500 }}>
                        {fmtMoney(it.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={_exGxBox({
            marginTop: 12, padding: '10px 14px',
            background: HR.surfaceAlt, borderRadius: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            fontSize: FontSizes.xxs,
          })}>
            <span style={{ color: uiPick(HR.inkFaint, GxT.color.onV) }}>
              項目合計: <strong style={{ fontFamily: HR.mono, color: HR.ink }}>{fmtMoney(previewTotal)}</strong>
            </span>
            <Button size="sm" variant="ghost" icon="upload" onClick={() => fileRef.current?.click()}>
              重新選擇照片
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}


async function uploadReceiptFile(file) {
  const fd = new FormData();
  fd.append('file', file);
  const token = getToken();
  const res = await fetch('/api/expense/claims/receipt-upload', {
    method: 'POST',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = (typeof data?.detail === 'string') ? data.detail : `HTTP ${res.status}`;
    throw { status: res.status, detail };
  }
  return data;
}


/* 精靈步驟指示列 — ① 基本資訊 → ② 加入明細 → ③ 確認送出 */
const WIZARD_STEPS = [
  { n: 1, label: '基本資訊' },
  { n: 2, label: '加入明細' },
  { n: 3, label: '確認送出' },
];

function WizardSteps({ step }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 22, fontSize: FontSizes.xs }}>
      {WIZARD_STEPS.map((s, idx) => {
        const state = step > s.n ? 'done' : step === s.n ? 'on' : 'todo';
        const filled = state === 'done' || state === 'on';
        const color = state === 'todo' ? HR.inkFaint : HR.ink;
        return (
          <React.Fragment key={s.n}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color, fontWeight: state === 'on' ? 600 : 400 }}>
              <span style={{
                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                border: `1.5px solid ${filled ? HR.accent : HR.inkFaint}`,
                background: filled ? HR.accent : 'transparent',
                color: filled ? '#fff' : HR.inkFaint,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: FontSizes.xxs, fontFamily: HR.mono,
              }}>{state === 'done' ? '✓' : s.n}</span>
              {s.label}
            </span>
            {idx < WIZARD_STEPS.length - 1 && (
              <span style={{ flex: 1, height: 1, background: HR.ruleSoft, minWidth: 24 }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* 步驟 2 的兩種加入方式選格 */
function AddMethodCard({ icon, title, desc, accent, onClick, disabled }) {
  return (
    <button
      type="button" onClick={onClick} disabled={disabled}
      style={uiStyle({
        border: `1px solid ${accent ? HR.accent : HR.rule}`, borderRadius: 7, padding: '20px 16px',
        textAlign: 'center', background: accent ? HR.accentSoft : HR.surface,
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
        transition: 'border-color 120ms, background 120ms', font: 'inherit',
      }, {
        // tonal tiles (mockup .tile): primary-container for the recommended path, s2 otherwise
        borderRadius: GxT.radius.card, border: `1px solid ${accent ? 'transparent' : HR.rule}`,
        background: accent ? GxT.color.pc : GxT.color.s1,
      })}
      onMouseEnter={(e) => { if (disabled || accent) return; e.currentTarget.style.borderColor = HR.accent; }}
      onMouseLeave={(e) => { if (disabled || accent) return; e.currentTarget.style.borderColor = HR.rule; }}
    >
      <div style={{ color: HR.accent, display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
        <Icon name={icon} size={28} stroke={1.5} />
      </div>
      <div style={{ fontSize: FontSizes.sm, fontWeight: uiPick(600, 800), color: uiPick(HR.ink, accent ? GxT.color.onPc : GxT.color.on) }}>{title}</div>
      <div style={{ marginTop: 4, fontSize: FontSizes.xxs, color: HR.inkSoft }}>{desc}</div>
    </button>
  );
}

function ClaimForm({ categories, selfEmpId, onCancel, onSaved }) {
  const toast = useToast();
  const activeCats = categories.filter(c => c.active);
  const [step, setStep] = React.useState(1);
  const [title, setTitle] = React.useState('');
  const [claimDate, setClaimDate] = React.useState(todayStr());
  const [items, setItems] = React.useState([makeTmpItem(activeCats)]);
  const [submitting, setSubmitting] = React.useState(false);
  const [ocrOpen, setOcrOpen] = React.useState(false);

  const addItem = () => setItems(arr => [...arr, makeTmpItem(activeCats)]);
  const removeItem = (i) => setItems(arr => arr.filter((_, idx) => idx !== i));
  const updItem = (i, k, v) => setItems(arr => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  const total = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
  const validItems = items.filter(i => parseFloat(i.amount) > 0);

  const goNext = () => {
    if (step === 1) {
      if (!title.trim()) { toast('請填單據標題', 'error'); return; }
      setStep(2); return;
    }
    if (step === 2) {
      if (validItems.length === 0) { toast('請至少加入一筆金額 > 0 的明細', 'error'); return; }
      setStep(3); return;
    }
  };
  const goBack = () => setStep(s => Math.max(1, s - 1));

  const save = async (asDraft) => {
    if (!title.trim()) { toast('請填單據標題', 'error'); setStep(1); return; }
    if (!asDraft && validItems.length === 0) { toast('送簽前至少需一筆金額 > 0 的項目', 'error'); setStep(2); return; }
    if (!selfEmpId) { toast('找不到您的員工記錄，請聯絡 HR', 'error'); return; }


    setSubmitting(true);
    let claimId = null;
    try {
      const claim = await api('POST', '/expense/claims', {
        employee_id: selfEmpId,
        title: title.trim(),
        claim_date: claimDate || null,
        currency: 'TWD',
      });
      claimId = claim.id;

      for (const it of validItems) {
        await api('POST', `/expense/claims/${claimId}/items`, {
          category_id: it.category_id || null,
          expense_date: it.expense_date || null,
          amount: parseFloat(it.amount),
          tax_amount: 0,
          description: it.description?.trim() || null,
          attachment_path: it.attachment_path || null,
        });
      }

      if (!asDraft) {
        await api('POST', `/expense/claims/${claimId}/submit`);
      }

      toast(asDraft ? '已存為草稿' : '已送出申請', 'ok');
      onSaved();
    } catch (e) {
      toast((asDraft ? '存草稿' : '送簽') + '失敗：' + (e.detail || ''), 'error');
      if (claimId) toast(`已建立草稿單 #${claimId}，可至「我的報帳」繼續編輯。`, 'info');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={onCancel}
      title="新增報帳單"
      subtitle={`New Expense Claim · 步驟 ${step} / 3`}
      size="lg"
      footer={
        <>
          {step === 1
            ? <Button variant="ghost" onClick={onCancel} disabled={submitting}>取消</Button>
            : <Button variant="ghost" onClick={goBack} disabled={submitting}>← 上一步</Button>}
          <span style={{ flex: 1 }} />
          {step >= 2 && (
            <Button variant="secondary" onClick={() => save(true)} disabled={submitting}>{submitting ? '處理中…' : '存草稿'}</Button>
          )}
          {step < 3
            ? <Button variant="primary" onClick={goNext} disabled={submitting}>下一步 →</Button>
            : <Button variant="primary" onClick={() => save(false)} disabled={submitting}>{submitting ? '處理中…' : '送出申請'}</Button>}
        </>
      }
    >
      <WizardSteps step={step} />

      {/* ── 步驟 1：基本資訊 ── */}
      {step === 1 && (
        <div className="hr-expense-claim-form-header" style={{ display: 'grid', gridTemplateColumns: '1fr 180px', gap: 14 }}>
          <Field label="單據標題" required>
            <TextInput value={title} onChange={setTitle} placeholder="e.g. 客戶拜訪 — 新竹" />
          </Field>
          <Field label="申請日">
            <input type="date" value={claimDate} onChange={(e) => setClaimDate(e.target.value)} style={inputStyle()} />
          </Field>
        </div>
      )}

      {/* ── 步驟 2：加入明細 ── */}
      {step === 2 && (
        <>
          <p style={{ fontSize: FontSizes.xs, color: HR.inkSoft, marginBottom: 8 }}>怎麼加入這次的費用明細？</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
            <AddMethodCard accent icon="upload" title="拍 / 上傳收據" desc="OCR 自動辨識，省去手 key" onClick={() => setOcrOpen(true)} disabled={submitting} />
            <AddMethodCard icon="plus" title="手動輸入" desc="自行填寫類別、金額與說明" onClick={addItem} disabled={submitting} />
          </div>
          <ItemEditTable
            items={items}
            categories={activeCats}
            updItem={updItem}
            removeItem={removeItem}
            total={total}
          />
        </>
      )}

      {/* ── 步驟 3：確認送出 ── */}
      {step === 3 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
            <div>
              <div style={_exGxLabel({ fontSize: FontSizes.trading_lg, letterSpacing: '0.08em', textTransform: 'uppercase', color: HR.inkFaint, marginBottom: 4 })}>單據標題</div>
              <div style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.md }, { fontWeight: 700 })}>{title.trim() || '—'}</div>
            </div>
            <div>
              <div style={_exGxLabel({ fontSize: FontSizes.trading_lg, letterSpacing: '0.08em', textTransform: 'uppercase', color: HR.inkFaint, marginBottom: 4 })}>申請日</div>
              <div style={{ fontFamily: HR.mono, fontSize: FontSizes.sm }}>{claimDate || '—'}</div>
            </div>
          </div>
          <div style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.md, marginBottom: 10 }, { fontWeight: 800 })}>明細項目 · {validItems.length} 筆</div>
          <ItemEditTable
            items={validItems}
            categories={activeCats}
            updItem={() => {}}
            removeItem={() => {}}
            total={validItems.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0)}
            readonly
          />
          <p style={_exGxUpright({ fontSize: FontSizes.xxs, color: HR.inkFaint, marginTop: 10, fontStyle: 'italic' })}>
            送出後將進入簽核流程；如需修改可改存草稿，稍後於「我的報帳」編輯。
          </p>
        </>
      )}

      {ocrOpen && (
        <ExpenseOcrModal
          categories={activeCats}
          onCancel={() => setOcrOpen(false)}
          onConfirm={(ocrItems, mode) => {
            if (mode === 'overwrite') {
              setItems(ocrItems);
            } else {
              if (items.length === 1 && parseFloat(items[0].amount) === 0 && !items[0].description) {
                setItems(ocrItems);
              } else {
                setItems(prev => [...prev, ...ocrItems]);
              }
            }
            setOcrOpen(false);
          }}
        />
      )}
    </Modal>
  );
}

function ItemReceiptUpload({ attachmentPath, onUpload, onRemove, disabled }) {
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef(null);
  const toast = useToast();

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast('檔案上限 10 MB', 'error');
      return;
    }
    setUploading(true);
    try {
      const res = await uploadReceiptFile(file);
      onUpload(res.path);
    } catch (err) {
      toast('上傳失敗：' + (err.detail || err.message || ''), 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (attachmentPath) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: FontSizes.xxs }}>
        <ReceiptLink path={attachmentPath} />
        {!disabled && (
          <button
            onClick={onRemove}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: HR.down,
              fontSize: FontSizes.sm,
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center'
            }}
            title="移除"
          >
            ×
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <input
        type="file"
        ref={fileInputRef}
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        onChange={handleChange}
        disabled={disabled || uploading}
        style={{ display: 'none' }}
      />
      <Button
        size="sm"
        variant="ghost"
        disabled={disabled || uploading}
        onClick={() => fileInputRef.current?.click()}
        style={{ padding: '3px 8px', fontSize: FontSizes.xxs }}
      >
        {uploading ? '上傳中…' : '選擇檔案'}
      </Button>
    </div>
  );
}

function ItemEditTable({ items, categories, updItem, removeItem, total, readonly }) {
  return (
    <div className="hr-expense-item-table" style={uiStyle({ border: `1px solid ${HR.rule}`, borderRadius: 5, overflow: 'hidden' }, { borderRadius: GxT.radius.inner })}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
        <thead>
          <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.08em', textTransform: 'uppercase', color: HR.inkFaint }}>
            <th style={{ padding: '10px 12px', textAlign: 'left',  fontWeight: 500, width: 120 }}>類別</th>
            <th style={{ padding: '10px 12px', textAlign: 'left',  fontWeight: 500, width: 120 }}>日期</th>
            <th style={{ padding: '10px 12px', textAlign: 'left',  fontWeight: 500 }}>說明</th>
            <th style={{ padding: '10px 12px', textAlign: 'left',  fontWeight: 500, width: 160 }}>收據憑證</th>
            <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 500, width: 95 }}>金額</th>
            {!readonly && <th style={{ padding: '10px 8px', width: 32 }}></th>}
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={it.tmpId || it.id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
              <td style={{ padding: '8px 12px' }}>
                <select
                  value={it.category_id || ''}
                  onChange={(e) => updItem(i, 'category_id', e.target.value ? parseInt(e.target.value) : null)}
                  style={inputStyle({ padding: '5px 8px', fontSize: FontSizes.xxs })}
                  disabled={readonly}
                >
                  <option value="">—</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </td>
              <td style={{ padding: '8px 12px' }}>
                <input type="date" value={it.expense_date || ''} onChange={(e) => updItem(i, 'expense_date', e.target.value)} disabled={readonly} style={inputStyle({ padding: '5px 8px', fontSize: FontSizes.xxs })} />
              </td>
              <td style={{ padding: '8px 12px' }}>
                <input value={it.description || ''} onChange={(e) => updItem(i, 'description', e.target.value)} disabled={readonly} placeholder="說明…" style={inputStyle({ padding: '5px 8px', fontSize: FontSizes.xxs })} />
              </td>
              <td style={{ padding: '8px 12px' }}>
                <ItemReceiptUpload
                  attachmentPath={it.attachment_path}
                  onUpload={(path) => updItem(i, 'attachment_path', path)}
                  onRemove={() => updItem(i, 'attachment_path', null)}
                  disabled={readonly}
                />
              </td>
              <td style={{ padding: '8px 12px' }}>
                <input type="number" value={it.amount} onChange={(e) => updItem(i, 'amount', e.target.value)} disabled={readonly} style={inputStyle({ padding: '5px 8px', fontSize: FontSizes.xxs, fontFamily: HR.mono, textAlign: 'right' })} />
              </td>
              {!readonly && (
                <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                  <button onClick={() => removeItem(i)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: HR.inkFaint, padding: 2, fontSize: FontSizes.md }} title="移除">×</button>
                </td>
              )}
            </tr>
          ))}
          <tr style={{ background: HR.surfaceAlt, borderTop: `2px solid ${HR.rule}` }}>
            <td colSpan={4} style={_exGxLabel({ padding: '12px 12px', textAlign: 'right', fontSize: FontSizes.trading_lg, letterSpacing: '0.08em', textTransform: 'uppercase', color: HR.inkFaint })}>合計</td>
            <td style={{ padding: '12px 12px', textAlign: 'right', fontFamily: HR.mono, fontWeight: uiPick(600, 800), fontSize: FontSizes.sm }}>{fmtMoney(total)}</td>
            {!readonly && <td></td>}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────  Draft editor (existing claim)  ───── */

function ClaimDraftEditor({ claim, categories, onClose, onChanged }) {
  const toast = useToast();
  const activeCats = categories.filter(c => c.active);
  const [items, setItems] = React.useState(claim.items || []);
  const [busy, setBusy] = React.useState(false);

  const reloadClaim = async () => {
    const fresh = await api('GET', `/expense/claims/${claim.id}`).catch(() => null);
    if (fresh) {
      setItems(fresh.items || []);
    }
  };

  const addItem = async () => {
    setBusy(true);
    try {
      await api('POST', `/expense/claims/${claim.id}/items`, {
        category_id: activeCats[0]?.id || null,
        expense_date: todayStr(),
        amount: 0.01,
        tax_amount: 0,
        description: '',
        attachment_path: null,
      });
      await reloadClaim();
      onChanged();
    } catch (e) { toast('新增失敗：' + (e.detail || ''), 'error'); }
    finally { setBusy(false); }
  };

  const updItem = async (i, k, v) => {
    const it = items[i];
    setItems(arr => arr.map((x, idx) => idx === i ? { ...x, [k]: v } : x));  // optimistic
    try {
      const body = { [k]: k === 'amount' ? parseFloat(v) || 0 : v };
      if (k === 'amount' && body.amount <= 0) {
        toast('金額需 > 0', 'error');
        await reloadClaim();
        return;
      }
      await api('PATCH', `/expense/items/${it.id}`, body);
      if (k === 'amount') await reloadClaim();  // refresh totals
      onChanged();
    } catch (e) { toast('更新失敗：' + (e.detail || ''), 'error'); await reloadClaim(); }
  };

  const removeItem = async (i) => {
    const it = items[i];
    if (!confirm('刪除此筆明細？')) return;
    try {
      await api('DELETE', `/expense/items/${it.id}`);
      await reloadClaim();
      onChanged();
    } catch (e) { toast('刪除失敗：' + (e.detail || ''), 'error'); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api('POST', `/expense/claims/${claim.id}/submit`);
      toast('已送出申請', 'ok');
      onChanged();
      onClose();
    } catch (e) { toast('送簽失敗：' + (e.detail || ''), 'error'); }
    finally { setBusy(false); }
  };

  const total = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);

  return (
    <Modal
      open onClose={onClose}
      title={`編輯草稿 — ${claim.title || claim.claim_no}`}
      subtitle={claim.claim_no}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>關閉</Button>
          <Button variant="primary" onClick={submit} disabled={busy || items.length === 0}>{busy ? '處理中…' : '送出申請'}</Button>
        </>
      }
    >
      <div style={{ fontSize: FontSizes.md, fontWeight: uiPick(500, 800), fontFamily: HR.serif, marginBottom: 18 }}>
        明細項目（編輯）
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ color: HR.inkFaint, fontSize: FontSizes.xxs }}>明細列表</span>
        <Button size="sm" variant="ghost" icon="plus" onClick={addItem} disabled={busy}>新增項目</Button>
      </div>
      <ItemEditTable
        items={items}
        categories={activeCats}
        updItem={updItem}
        removeItem={removeItem}
        total={total}
      />
    </Modal>
  );
}

/* ─────────────────────────  Decision (approve / reject) modal ───── */

function DecisionExpenseModal({ claim, action, onClose, onSaved }) {
  const toast = useToast();
  const [comment, setComment] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const isReject = action === 'reject';

  const submit = async () => {
    if (isReject && !comment.trim()) { toast('退回需填寫理由', 'error'); return; }
    setSubmitting(true);
    try {
      const body = isReject ? { comment: comment.trim() } : { comment: comment.trim() || null };
      await api('POST', `/expense/claims/${claim.id}/${action}`, body);
      toast(`${isReject ? '已退回' : '已核准'}「${claim.title || claim.claim_no}」`, isReject ? 'info' : 'ok');
      onSaved();
    } catch (e) {
      toast(`${isReject ? '退回' : '核准'}失敗：` + (e.detail || ''), 'error');
    } finally { setSubmitting(false); }
  };

  return (
    <Modal
      open onClose={onClose}
      title={isReject ? '退回報帳單' : '核准報帳單'}
      subtitle={claim.claim_no}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>取消</Button>
          <Button
            variant="primary" onClick={submit} disabled={submitting}
            style={isReject ? { background: HR.down, boxShadow: '0 1px 0 rgba(155,51,48,0.3)' } : undefined}
          >
            {submitting ? '處理中…' : (isReject ? '確認退回' : '確認核准')}
          </Button>
        </>
      }
    >
      <div style={_exGxBox({ marginBottom: 14, padding: 12, background: HR.surfaceAlt, borderRadius: 4, fontSize: FontSizes.trading_md, color: HR.inkSoft, lineHeight: 1.6 })}>
        <div><strong style={{ color: HR.ink }}>{claim.title || '（無標題）'}</strong> · {claim.employee_name || `#${claim.employee_id}`}</div>
        <div style={{ fontFamily: HR.mono, marginTop: 2 }}>{fmtMoney(claim.total_amount)} · {(claim.items?.length ?? 0)} 項</div>
      </div>
      <Field label={isReject ? '退回理由' : '核准備註'} required={isReject}>
        <Textarea value={comment} onChange={setComment} rows={3} placeholder={isReject ? '請填寫退回原因…' : '（選填）'} />
      </Field>
    </Modal>
  );
}

/* ─────────────────────────  Pay modal (finance/admin) ─────────── */

function PayModal({ claim, onClose, onSaved }) {
  const toast = useToast();
  const [method, setMethod] = React.useState('bank_transfer');
  const [paidAt, setPaidAt] = React.useState(todayStr());
  const [remark, setRemark] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api('POST', `/expense/claims/${claim.id}/pay`, {
        payment_method: method,
        paid_at: paidAt,
        remark: remark.trim() || null,
      });
      toast(`已標記「${claim.title || claim.claim_no}」為已付款`, 'ok');
      onSaved();
    } catch (e) {
      toast('付款失敗：' + (e.detail || ''), 'error');
    } finally { setSubmitting(false); }
  };

  return (
    <Modal
      open onClose={onClose}
      title="標記已付款"
      subtitle={claim.claim_no}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>{submitting ? '處理中…' : '確認付款'}</Button>
        </>
      }
    >
      <div style={_exGxBox({ marginBottom: 14, padding: 12, background: HR.surfaceAlt, borderRadius: 4, fontSize: FontSizes.trading_md, color: HR.inkSoft })}>
        <div><strong style={{ color: HR.ink }}>{claim.title}</strong></div>
        <div style={{ fontFamily: HR.mono, marginTop: 2 }}>{fmtMoney(claim.total_amount)}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="付款方式">
          <select value={method} onChange={(e) => setMethod(e.target.value)} style={inputStyle()}>
            <option value="bank_transfer">銀行轉帳</option>
            <option value="cash">現金</option>
            <option value="company_card">公司卡</option>
            <option value="check">支票</option>
          </select>
        </Field>
        <Field label="實付日">
          <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} style={inputStyle()} />
        </Field>
        <Field label="核銷備註" span={2} hint="會出現在報帳月報的明細欄。員工不可編輯此欄。">
          <Textarea value={remark} onChange={setRemark} rows={2} placeholder="（選填）轉帳備註 / 折讓 / 例外處理…" />
        </Field>
      </div>
    </Modal>
  );
}

/* ─────────────────────────  我的報帳  ───────────────────────── */

function MyExpenseTab({ categories, claims, selfEmpId, onRefresh }) {
  const toast = useToast();
  const [openForm, setOpenForm] = React.useState(false);
  const [expanded, setExpanded] = React.useState(null);
  const [editDraft, setEditDraft] = React.useState(null);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api('DELETE', `/expense/claims/${deleteTarget.id}`);
      toast('已刪除草稿', 'ok');
      setDeleteTarget(null);
      onRefresh();
    } catch (e) { toast('刪除失敗：' + (e.detail || ''), 'error'); }
    finally { setDeleting(false); }
  };

  return (
    <>
      <Card className="hr-expense-my-card" padding="0" eyebrow="My Claims" title="我的報帳單" italic={`${claims.length} 筆`} right={
        <Button variant="primary" size="md" icon="plus" onClick={() => setOpenForm(true)} disabled={!selfEmpId}>新增報帳</Button>
      }>
        {claims.length === 0 ? (
          <EmptyState icon="dollar-sign" title="尚無報帳紀錄" hint={selfEmpId ? '點右上角新增。' : '您尚未對應員工記錄，請聯絡 HR。'} />
        ) : (
          <div>
            {claims.map(c => {
              const isOpen = expanded === c.id;
              const status = CLAIM_STATUS[c.status] || CLAIM_STATUS.draft;
              const isDraft = c.status === 'draft';
              return (
                <div key={c.id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                  <div
                    onClick={() => setExpanded(isOpen ? null : c.id)}
                    className="hr-expense-claim-row"
                    style={{
                      display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 18,
                      padding: '16px 24px', cursor: 'pointer', alignItems: 'center',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = HR.surfaceAlt}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div>
                      <div style={{ fontFamily: HR.serif, fontSize: FontSizes.md, fontWeight: uiPick(500, 700) }}>{c.title || '（無標題）'}</div>
                      <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 3, fontFamily: HR.mono }}>
                        {c.claim_no} · {c.submitted_at ? `送出於 ${fmtDateTime(c.submitted_at)}` : `建立於 ${fmtDateTime(c.created_at)}`} · {(c.items?.length ?? 0)} 項
                        {c.status === 'submitted' && (
                          <span style={{ color: '#8A5A1A', marginLeft: 8 }}>
                            · 等待 {((c.approvals || []).find(a => a.status === 'pending')?.approver_name) || '管理員'} 審核
                          </span>
                        )}
                      </div>
                    </div>
                    <Pill tone={status.tone} dot>{status.label}</Pill>
                    <div style={{ fontFamily: HR.mono, fontSize: FontSizes.md, fontWeight: uiPick(600, 700), textAlign: 'right' }}>
                      {fmtMoney(c.total_amount)}
                    </div>
                    <Icon name="chevron-down" size={14} className={isOpen ? 'rot' : ''} />
                  </div>
                  {isOpen && (
                    <div style={{ padding: '4px 24px 22px 24px', background: HR.surfaceAlt }}>
                      {(() => {
                        const attachments = (c.items || []).map(it => it.attachment_path).filter(Boolean);
                        const uniqueAttachments = Array.from(new Set(attachments));
                        if (uniqueAttachments.length === 0) return null;
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '10px 0', borderBottom: `1px solid ${HR.ruleSoft}`, marginBottom: 10 }}>
                            <span style={{ fontSize: FontSizes.xxs, color: HR.inkSoft }}>收據憑證：</span>
                            {uniqueAttachments.map((path, idx) => (
                              <React.Fragment key={path}>
                                {idx > 0 && <span style={{ color: HR.rule }}>|</span>}
                                <ReceiptLink path={path} />
                              </React.Fragment>
                            ))}
                          </div>
                        );
                      })()}
                      <ItemReadOnlyTable items={c.items || []} />
                      {(c.approvals || []).length > 0 && (
                        <div style={uiStyle({ marginTop: 14, padding: 12, background: HR.surface, borderRadius: 4, border: `1px solid ${HR.rule}` }, { padding: '12px 14px', borderRadius: GxT.radius.inner, border: 'none' })}>
                          <div style={_exGxLabel({ fontSize: FontSizes.trading_lg, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint, marginBottom: 8 })}>簽核紀錄</div>
                          {c.approvals.map(a => (
                            <div key={a.id} style={{ fontSize: FontSizes.xxs, color: HR.inkSoft, marginBottom: 4 }}>
                              <strong style={{ color: HR.ink }}>{a.approver_name || <span style={{ color: HR.inkFaint, fontWeight: 400 }}>—</span>}</strong>
                              <span style={{ color: HR.inkFaint }}> · {a.status}{a.decided_at && ` · ${fmtDateTime(a.decided_at)}`}</span>
                              {a.comment && <div style={_exGxUpright({ fontStyle: 'italic', fontFamily: HR.serif, marginLeft: 8 })}>「{a.comment}」</div>}
                            </div>
                          ))}
                        </div>
                      )}
                      {isDraft && (
                        <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(c)} style={{ color: HR.down }}>刪除草稿</Button>
                          <Button size="sm" variant="primary" onClick={() => setEditDraft(c)}>編輯並送簽</Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
      {openForm && (
        <ClaimForm
          categories={categories} selfEmpId={selfEmpId}
          onCancel={() => setOpenForm(false)}
          onSaved={() => { setOpenForm(false); onRefresh(); }}
        />
      )}
      {editDraft && (
        <ClaimDraftEditor
          claim={editDraft} categories={categories}
          onClose={() => setEditDraft(null)}
          onChanged={onRefresh}
        />
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        title="刪除草稿"
        message={deleteTarget ? `確定刪除草稿單「${deleteTarget.title || deleteTarget.claim_no}」？此操作無法復原。` : ''}
        danger busy={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={doDelete}
      />
    </>
  );
}

function ItemReadOnlyTable({ items }) {
  if (!items || items.length === 0) return <div style={_exGxUpright({ padding: 16, fontSize: FontSizes.xxs, color: HR.inkFaint, fontStyle: 'italic' })}>（尚無明細）</div>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.trading_md }}>
      <thead>
        <tr style={{ fontSize: FontSizes.trading_sm, letterSpacing: '0.08em', textTransform: 'uppercase', color: HR.inkFaint }}>
          <th style={{ padding: '8px 10px', textAlign: 'left',  fontWeight: 500 }}>類別</th>
          <th style={{ padding: '8px 10px', textAlign: 'left',  fontWeight: 500 }}>日期</th>
          <th style={{ padding: '8px 10px', textAlign: 'left',  fontWeight: 500 }}>說明</th>
          <th style={{ padding: '8px 10px', textAlign: 'left',  fontWeight: 500, width: 160 }}>憑證</th>
          <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500 }}>金額</th>
        </tr>
      </thead>
      <tbody>
        {items.map(it => (
          <tr key={it.id} style={{ borderTop: `1px solid ${HR.rule}` }}>
            <td style={{ padding: '8px 10px' }}>{it.category_name || it.category_code || '—'}</td>
            <td style={{ padding: '8px 10px', fontFamily: HR.mono, color: HR.inkSoft }}>{it.expense_date || '—'}</td>
            <td style={{ padding: '8px 10px', color: HR.inkSoft }}>{it.description || '—'}</td>
            <td style={{ padding: '8px 10px' }}><ReceiptLink path={it.attachment_path} /></td>
            <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: HR.mono }}>{fmtMoney(it.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ─────────────────────────  審核佇列  ───────────────────────── */

function ApprovalsExpenseTab({ pendingClaims, paidableClaims, user, onRefresh }) {
  const [decision, setDecision] = React.useState(null);
  const [payTarget, setPayTarget] = React.useState(null);
  const canApprove = hasCapability(user, 'expense.approve');
  const canPay = hasCapability(user, 'expense.pay');
  const pendingTotal = pendingClaims.reduce((s, c) => s + (c.total_amount || 0), 0);

  return (
    <>
      <Card className="hr-expense-approvals-card" padding="0" eyebrow="Approval Queue" title="待審核報帳" italic={`${pendingClaims.length} 筆 · 共 ${fmtMoney(pendingTotal)}`}>
        {pendingClaims.length === 0 ? (
          <EmptyState icon="dollar-sign" title="目前沒有待審核項目" hint="所有報帳單都已處理完畢。" />
        ) : (
          <div>
            {pendingClaims.map(c => (
              <div key={c.id} className="hr-expense-approval-item" style={{
                padding: '18px 24px', borderTop: `1px solid ${HR.ruleSoft}`,
                display: 'grid', gridTemplateColumns: '40px 1fr auto', gap: 16, alignItems: 'flex-start',
              }}>
                <div style={_exGxAvatar({ width: 36, height: 36, borderRadius: '50%', background: '#D6CEB9', display: 'grid', placeItems: 'center', fontSize: FontSizes.xxs, fontWeight: 600 })}>
                  {(c.employee_name || '？').slice(0, 1)}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: HR.serif, fontSize: FontSizes.base, fontWeight: uiPick(500, 700) }}>{c.title || '（無標題）'}</span>
                    <span style={{ fontSize: FontSizes.xxs, color: HR.inkSoft }}>by {c.employee_name || `#${c.employee_id}`}</span>
                    <span style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontFamily: HR.mono }}>{c.claim_no}</span>
                  </div>
                  <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontFamily: HR.mono, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>{(c.items?.length ?? 0)} 項</span> · 
                    <span>送出 {fmtDateTime(c.submitted_at || c.created_at)}</span>
                    {(() => {
                      const attachments = (c.items || []).map(it => it.attachment_path).filter(Boolean);
                      const uniqueAttachments = Array.from(new Set(attachments));
                      if (uniqueAttachments.length === 0) return null;
                      return (
                        <>
                          · {uniqueAttachments.map((path, idx) => (
                            <React.Fragment key={path}>
                              {idx > 0 && <span style={{ color: HR.rule, margin: '0 4px' }}>|</span>}
                              <ReceiptLink path={path} />
                            </React.Fragment>
                          ))}
                        </>
                      );
                    })()}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {(c.items || []).map(it => (
                      <span key={it.id} style={uiStyle({
                        padding: '3px 8px', fontSize: FontSizes.trading_lg, background: HR.ruleSoft, borderRadius: 3,
                        fontFamily: HR.mono, color: HR.inkSoft,
                      }, { padding: '3px 10px', background: GxT.color.s2, borderRadius: GxT.radius.chipSm })}>{it.category_name || it.category_code || '—'} {fmtMoney(it.amount)}</span>
                    ))}
                  </div>
                </div>
                <div className="hr-expense-approval-actions" style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: HR.mono, fontSize: FontSizes.lg, fontWeight: uiPick(600, 800), marginBottom: 10 }}>{fmtMoney(c.total_amount)}</div>
                  {canApprove && (
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <Button size="sm" variant="ghost"   onClick={() => setDecision({ claim: c, action: 'reject' })}  style={{ color: HR.down }}>退回</Button>
                      <Button size="sm" variant="primary" onClick={() => setDecision({ claim: c, action: 'approve' })}>核准</Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {canPay && (
        <div style={{ marginTop: 24 }}>
          <Card className="hr-expense-paidable-card" padding="0" eyebrow="Approved · Pending Payment" title="待付款" italic={`${paidableClaims.length} 筆`}>
            {paidableClaims.length === 0 ? (
              <EmptyState icon="dollar-sign" title="目前沒有待付款項目" hint="所有核准單均已付款。" />
            ) : (
              <div className="hr-expense-paidable-table">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
                <thead>
                  <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                    <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left',  fontWeight: 500 }}>單號</th>
                    <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>申請人</th>
                    <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>標題</th>
                    <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>金額</th>
                    <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {paidableClaims.map(c => (
                    <tr key={c.id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                      <td style={{ padding: '13px 16px 13px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>{c.claim_no}</td>
                      <td style={{ padding: '13px 16px' }}>{c.employee_name || <span style={{ color: HR.inkFaint }}>—</span>}</td>
                      <td style={{ padding: '13px 16px', fontFamily: HR.serif }}>
                        <div>{c.title || '—'}</div>
                        {(() => {
                          const attachments = (c.items || []).map(it => it.attachment_path).filter(Boolean);
                          const uniqueAttachments = Array.from(new Set(attachments));
                          if (uniqueAttachments.length === 0) return null;
                          return (
                            <div style={{ marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {uniqueAttachments.map((path, idx) => (
                                <React.Fragment key={path}>
                                  {idx > 0 && <span style={{ color: HR.rule }}>|</span>}
                                  <ReceiptLink path={path} />
                                </React.Fragment>
                              ))}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>{fmtMoney(c.total_amount)}</td>
                      <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right' }}>
                        <Button size="sm" variant="primary" onClick={() => setPayTarget(c)}>標記付款</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {decision && (
        <DecisionExpenseModal
          claim={decision.claim} action={decision.action}
          onClose={() => setDecision(null)}
          onSaved={() => { setDecision(null); onRefresh(); }}
        />
      )}
      {payTarget && (
        <PayModal
          claim={payTarget}
          onClose={() => setPayTarget(null)}
          onSaved={() => { setPayTarget(null); onRefresh(); }}
        />
      )}
    </>
  );
}

/* ─────────────────────────  類別管理  ───────────────────────── */

function CategoryForm({ initial, onClose, onSaved }) {
  const toast = useToast();
  const isEdit = !!initial?.id;
  const [code, setCode] = React.useState(initial?.code || '');
  const [name, setName] = React.useState(initial?.name || '');
  const [requires_invoice, setRequiresInvoice] = React.useState(initial?.requires_invoice ?? true);
  const [gl_account_code, setGlCode] = React.useState(initial?.gl_account_code || '');
  const [active, setActive] = React.useState(initial?.active ?? true);
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    if (!code.trim() || !name.trim()) { toast('代碼與名稱必填', 'error'); return; }
    setSubmitting(true);
    try {
      const body = {
        code: code.trim(), name: name.trim(),
        requires_invoice, active,
        gl_account_code: gl_account_code.trim() || null,
      };
      if (isEdit) await api('PATCH', `/expense/categories/${initial.id}`, body);
      else        await api('POST',  '/expense/categories', body);
      toast(isEdit ? '已更新' : '已新增', 'ok');
      onSaved();
    } catch (e) { toast((isEdit ? '更新' : '新增') + '失敗：' + (e.detail || ''), 'error'); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal
      open onClose={onClose}
      title={isEdit ? `編輯類別 — ${initial.name}` : '新增類別'}
      subtitle="Expense Category"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>{submitting ? '儲存中…' : (isEdit ? '儲存' : '新增')}</Button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="代碼" required hint="英文小寫，例：meal / transport">
          <TextInput value={code} onChange={setCode} mono />
        </Field>
        <Field label="名稱" required>
          <TextInput value={name} onChange={setName} />
        </Field>
        <Field label="需發票">
          <select value={requires_invoice ? '1' : '0'} onChange={(e) => setRequiresInvoice(e.target.value === '1')} style={inputStyle()}>
            <option value="1">是</option>
            <option value="0">否</option>
          </select>
        </Field>
        <Field label="會計科目">
          <TextInput value={gl_account_code} onChange={setGlCode} mono placeholder="（選填）" />
        </Field>
        <Field label="啟用" span={2}>
          <select value={active ? '1' : '0'} onChange={(e) => setActive(e.target.value === '1')} style={inputStyle()}>
            <option value="1">啟用中</option>
            <option value="0">已停用</option>
          </select>
        </Field>
      </div>
    </Modal>
  );
}

function CategoriesTab({ categories, onRefresh, canEdit }) {
  const [editing, setEditing] = React.useState(null);

  return (
    <>
      <Card className="hr-expense-categories-card" padding="0" eyebrow="Categories" title="報帳類別" italic={`${categories.length} 種`} right={
        canEdit && <Button variant="primary" size="md" icon="plus" onClick={() => setEditing({})}>新增類別</Button>
      }>
        {categories.length === 0 ? (
          <EmptyState icon="settings" title="尚未建立類別" hint="點右上角新增。" />
        ) : (
          <div className="hr-expense-categories-table">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
            <thead>
              <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left', fontWeight: 500 }}>代碼</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left', fontWeight: 500 }}>名稱</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left', fontWeight: 500 }}>需發票</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left', fontWeight: 500 }}>會計科目</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left', fontWeight: 500 }}>狀態</th>
                {canEdit && <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>操作</th>}
              </tr>
            </thead>
            <tbody>
              {categories.map(c => (
                <tr key={c.id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                  <td style={{ padding: '13px 16px 13px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>{c.code}</td>
                  <td style={{ padding: '13px 16px', fontWeight: 500 }}>{c.name}</td>
                  <td style={{ padding: '13px 16px' }}>
                    {c.requires_invoice ? <Pill tone="warn" dot>需附</Pill> : <span style={{ color: HR.inkFaint, fontSize: FontSizes.xxs }}>不需</span>}
                  </td>
                  <td style={{ padding: '13px 16px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>{c.gl_account_code || '—'}</td>
                  <td style={{ padding: '13px 16px' }}>
                    {c.active ? <Pill tone="accent" dot>啟用中</Pill> : <Pill tone="neutral">已停用</Pill>}
                  </td>
                  {canEdit && (
                    <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right' }}>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(c)}>編輯</Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>
      {editing !== null && (
        <CategoryForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onRefresh(); }} />
      )}
    </>
  );
}

/* ─────────────────────────  Page entry  ─────────────────────── */

function ExpensePage() {
  const [user, setUser] = React.useState(null);
  const [selfEmp, setSelfEmp] = React.useState(null);
  const [categories, setCategories] = React.useState([]);
  const [myClaims, setMyClaims] = React.useState([]);
  const [pendingClaims, setPendingClaims] = React.useState([]);
  const [paidableClaims, setPaidableClaims] = React.useState([]);
  const [paidClaims, setPaidClaims] = React.useState([]);
  const [tab, setTab] = React.useState('me');
  const [loading, setLoading] = React.useState(true);

  // Capability gates — 對應後端 routes_expense.py 的 require_role
  const canApprove = hasCapability(user, 'expense.approve');
  const canPay = hasCapability(user, 'expense.pay');
  const canManageCats = hasCapability(user, 'expense.category.manage');
  // 「審核佇列」要顯示給 approver 跟 payer 任一（pay 也要看核准單）
  const canSeeQueue = canApprove || canPay;
  const isHR = canApprove;  // 給 header italic 用

  const refresh = React.useCallback(async (u) => {
    const who = u || user;
    const whoCanManageCats = hasCapability(who, 'expense.category.manage');
    const whoCanSeeQueue = hasAnyCapability(who, 'expense.approve', 'expense.pay');
    const [myClaimsResp, catsResp] = await Promise.all([
      api('GET', '/expense/claims/me?page_size=100').catch(() => ({ items: [] })),
      api('GET', `/expense/categories?active_only=${whoCanManageCats ? 'false' : 'true'}`).catch(() => []),
    ]);
    setMyClaims(myClaimsResp.items || []);
    setCategories(Array.isArray(catsResp) ? catsResp : []);

    if (whoCanSeeQueue) {
      const [pendResp, approvedResp, paidResp] = await Promise.all([
        api('GET', '/expense/claims?status=submitted&page_size=100').catch(() => ({ items: [] })),
        api('GET', '/expense/claims?status=approved&page_size=100').catch(() => ({ items: [] })),
        api('GET', '/expense/claims?status=paid&page_size=200').catch(() => ({ items: [] })),
      ]);
      setPendingClaims(pendResp.items || []);
      setPaidableClaims(approvedResp.items || []);
      setPaidClaims(paidResp.items || []);
    } else {
      setPendingClaims([]);
      setPaidableClaims([]);
      setPaidClaims([]);
    }
  }, [user]);

  React.useEffect(() => {
    let alive = true;
    Promise.all([
      api('GET', '/auth/me').catch(() => null),
      api('GET', '/employees/me').catch(() => null),
    ]).then(async ([u, emp]) => {
      if (!alive) return;
      setUser(u);
      setSelfEmp(emp);
      if (u) await refresh(u);
      if (alive) setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const pendingCount = pendingClaims.length;
  const tabs = [
    { key: 'me', label: '我的報帳', icon: 'dollar-sign' },
    ...(canSeeQueue ? [
      { key: 'approvals', label: '審核佇列', icon: 'arrow-right', count: pendingCount },
      { key: 'paid',      label: '付款歷史', icon: 'check' },
    ] : []),
    ...(canManageCats ? [
      { key: 'categories', label: '類別管理', icon: 'settings' },
    ] : []),
  ];

  return (
    <>
      <PageHeader
        eyebrow="財務 · Expense"
        title="報帳"
        italic={isHR ? 'Expense Console' : 'My Claims'}
      />
      <Tabs tabs={tabs} value={tab} onChange={setTab} />
      {loading ? (
        <SkeletonTable rows={6} />
      ) : (
        <>
          {tab === 'me'         && <MyExpenseTab        categories={categories} claims={myClaims} selfEmpId={selfEmp?.id} onRefresh={() => refresh()} />}
          {tab === 'approvals'  && <ApprovalsExpenseTab pendingClaims={pendingClaims} paidableClaims={paidableClaims} user={user} onRefresh={() => refresh()} />}
          {tab === 'paid'       && <PaidHistoryTab      paidClaims={paidClaims} />}
          {tab === 'categories' && <CategoriesTab       categories={categories} canEdit={canManageCats} onRefresh={() => refresh()} />}
        </>
      )}
    </>
  );
}

/* ─────────────────────────  PaidHistoryTab  ─────────────────────
 * 已付款的 expense_claims — 顯示付款日期、方式、員工、金額、單號。
 * 後端 GET /expense/claims?status=paid 已可用。
 */
function PaidHistoryTab({ paidClaims }) {
  const [filterEmp, setFilterEmp] = React.useState('');
  const [filterMonth, setFilterMonth] = React.useState('');  // YYYY-MM

  const filtered = (paidClaims || []).filter(c => {
    if (filterEmp && !(c.employee_name || '').includes(filterEmp)) return false;
    if (filterMonth && !(c.paid_at || '').startsWith(filterMonth)) return false;
    return true;
  });
  const totalAmount = filtered.reduce((s, c) => s + (Number(c.total_amount) || 0), 0);

  return (
    <>
      <div className="hr-expense-filter-bar" style={uiStyle({
        display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14,
        padding: '10px 14px', background: HR.surface,
        border: `1px solid ${HR.rule}`, borderRadius: 6,
      }, { padding: '12px 16px', border: 'none', borderRadius: GxT.radius.card })}>
        <span style={_exGxLabel({ fontSize: FontSizes.trading_lg, color: HR.inkFaint, letterSpacing: '0.1em', textTransform: 'uppercase' })}>
          篩選
        </span>
        <input
          type="month"
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          style={inputStyle({ padding: '6px 10px', fontSize: FontSizes.xxs, width: 140 })}
        />
        <input
          type="text"
          placeholder="員工姓名"
          value={filterEmp}
          onChange={(e) => setFilterEmp(e.target.value)}
          style={inputStyle({ padding: '6px 10px', fontSize: FontSizes.xxs, width: 160 })}
        />
        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>
          共 {filtered.length} 筆 · 合計 {fmtMoney(totalAmount)}
        </span>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="check" title="無付款紀錄" hint="付款後的報帳單會出現在這裡" />
      ) : (
        <Card className="hr-expense-paid-card" padding="0">
          <div className="hr-expense-paid-table">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
            <thead>
              <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left', fontWeight: 500 }}>單號</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>員工</th>
                <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>金額</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>付款方式</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>付款日</th>
                <th style={{ padding: '11px 24px 11px 16px', textAlign: 'left', fontWeight: 500 }}>送單日</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                  <td style={{ padding: '13px 16px 13px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs }}>{c.claim_no || `#${c.id}`}</td>
                  <td style={{ padding: '13px 16px', fontWeight: 500 }}>{c.employee_name || '—'}</td>
                  <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>
                    {fmtMoney(c.total_amount)}
                  </td>
                  <td style={{ padding: '13px 16px', color: HR.inkSoft }}>{c.payment_method || '—'}</td>
                  <td style={{ padding: '13px 16px', fontFamily: HR.mono, fontSize: FontSizes.xxs }}>
                    {c.paid_at ? c.paid_at.slice(0, 10) : '—'}
                  </td>
                  <td style={{ padding: '13px 24px 13px 16px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkFaint }}>
                    {(c.submitted_at || c.created_at || '').slice(0, 10)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      )}
    </>
  );
}

window.ExpensePage = ExpensePage;
